import { useEffect, useState } from 'react';
import { mapApi, missionAdminApi, type MissionData, type MissionLevelData } from '../../../api/adminApi';
import { fetchAdminItems } from '../../../api/adminItemApi';
import { useAuthStore } from '../../../auth/authStore';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/translations';
import type { Item } from '../../../types/interfaces';

// ─────────────────────────────────────────────────────────────────────────────
// Missões — /admin/missions
// Lista + editor de uma missão (níveis em ordem, custos, HP, checkpoint, prêmios).
// ─────────────────────────────────────────────────────────────────────────────

const WEATHERS = ['AUTO', 'SUNNY', 'SUNSET', 'NIGHT', 'CLOUDY', 'RAIN', 'FOG', 'STORM'] as const;

const newLevel = (monsterLevel: number): MissionLevelData => ({
  map_id: null, monster_level: monsterLevel, monster_count: null, weather_type: 'AUTO', reward_item_id: null,
});

const emptyMission = (): MissionData => ({
  name: '', active: true, levels: [newLevel(1)],
  energy_cost: 0, coin_cost: 0, hp_mode: 'renew',
  checkpoint_every: 0, checkpoint_ttl_hours: 24,
  drops_enabled: true, xp_enabled: true, completion_item_id: null,
  keep_rewards_on_loss: true, defeat_destination: 'hub', weather_effects: true,
});

export function MissionsPage() {
  const { t } = useTranslation();
  const { token } = useAuthStore();
  const [missions, setMissions] = useState<MissionData[]>([]);
  const [maps, setMaps] = useState<{ id?: string; name: string }[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [editing, setEditing] = useState<MissionData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const reload = () => missionAdminApi.list().then(setMissions).catch((e: Error) => setError(e.message));

  useEffect(() => {
    void reload();
    mapApi.list().then(setMaps).catch(() => setMaps([]));
    if (token) fetchAdminItems(token).then(setItems).catch(() => setItems([]));
  }, [token]);

  const patch = (p: Partial<MissionData>) => { setEditing(m => m ? { ...m, ...p } : m); setSaved(false); };
  const patchLevel = (i: number, p: Partial<MissionLevelData>) =>
    patch({ levels: editing!.levels.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  const moveLevel = (i: number, d: -1 | 1) => {
    const lv = [...editing!.levels];
    [lv[i], lv[i + d]] = [lv[i + d], lv[i]];
    patch({ levels: lv });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const res = editing.id ? await missionAdminApi.update(editing.id, editing) : await missionAdminApi.create(editing);
      setEditing(res);
      setSaved(true);
      void reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const itemSelect = (value: string | null, onChange: (v: string | null) => void) => (
    <select className="formInput" value={value ?? ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">{t('missions.none')}</option>
      {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
    </select>
  );

  const num = (label: TranslationKey, value: number, onChange: (v: number) => void, min = 0) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span className="formLabel" style={{ margin: 0, flex: 1 }}>{t(label)}</span>
      <input type="number" className="formInput" style={{ width: 110, textAlign: 'right' }} min={min}
             value={value} onChange={e => onChange(Math.max(min, parseInt(e.target.value, 10) || 0))} />
    </label>
  );

  const check = (label: TranslationKey, value: boolean, onChange: (v: boolean) => void) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
      <span className="formLabel" style={{ margin: 0 }}>{t(label)}</span>
    </label>
  );

  // ── Lista ──────────────────────────────────────────────────────────────────
  if (!editing) {
    return (
      <>
        <div className="adminHeader">
          <h1>{t('missions.title')}</h1>
          <button className="btn btnPrimary" onClick={() => { setEditing(emptyMission()); setSaved(false); }}>
            {t('missions.new')}
          </button>
        </div>
        <div className="adminContent">
          {error && <div className="alertError">{error}</div>}
          {missions.length === 0 ? (
            <p style={{ color: 'var(--text-dim)' }}>{t('missions.empty')}</p>
          ) : (
            <div className="adminCard">
              {missions.map(m => (
                <div key={m.id} onClick={() => { setEditing(m); setSaved(false); setError(''); }}
                     style={{ display: 'flex', gap: 12, padding: '12px 18px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ flex: 1, fontWeight: 600 }}>🌀 {m.name}</span>
                  <span style={{ color: 'var(--text-dim)' }}>{m.levels.length} {t('missions.levels')}</span>
                  <span style={{ color: m.active ? 'var(--success)' : 'var(--text-dim)' }}>
                    {m.active ? t('missions.active') : t('missions.inactive')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  // ── Editor ─────────────────────────────────────────────────────────────────
  const card = { padding: '16px 18px', display: 'flex', flexDirection: 'column' as const, gap: 14 };
  return (
    <>
      <div className="adminHeader">
        <h1>🌀 {editing.name || t('missions.new')}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>{t('missions.saved')}</span>}
          <button className="btn" onClick={() => setEditing(null)}>{t('missions.cancel')}</button>
          <button className="btn btnPrimary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? t('missions.saving') : t('missions.save')}
          </button>
        </div>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 20 }}>
          <div className="adminCard">
            <div className="adminCardHeader">{t('missions.card.general')}</div>
            <div style={card}>
              <label>
                <span className="formLabel">{t('missions.f.name')}</span>
                <input className="formInput" value={editing.name} onChange={e => patch({ name: e.target.value })} />
              </label>
              {check('missions.f.active', editing.active, v => patch({ active: v }))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="formLabel" style={{ margin: 0, flex: 1 }}>{t('missions.f.defeat')}</span>
                <select className="formInput" style={{ width: 170 }} value={editing.defeat_destination}
                        onChange={e => patch({ defeat_destination: e.target.value as MissionData['defeat_destination'] })}>
                  <option value="hub">{t('missions.defeat.hub')}</option>
                  <option value="dashboard">{t('missions.defeat.dashboard')}</option>
                </select>
              </label>
            </div>
          </div>

          <div className="adminCard">
            <div className="adminCardHeader">{t('missions.card.entry')}</div>
            <div style={card}>
              {num('missions.f.energy', editing.energy_cost, v => patch({ energy_cost: v }))}
              {num('missions.f.coin', editing.coin_cost, v => patch({ coin_cost: v }))}
            </div>
          </div>

          <div className="adminCard">
            <div className="adminCardHeader">{t('missions.card.progress')}</div>
            <div style={card}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="formLabel" style={{ margin: 0, flex: 1 }}>{t('missions.f.hpMode')}</span>
                <select className="formInput" style={{ width: 200 }} value={editing.hp_mode}
                        onChange={e => patch({ hp_mode: e.target.value as MissionData['hp_mode'] })}>
                  <option value="renew">{t('missions.hp.renew')}</option>
                  <option value="keep">{t('missions.hp.keep')}</option>
                </select>
              </label>
              {num('missions.f.checkpointEvery', editing.checkpoint_every, v => patch({ checkpoint_every: v }))}
              {num('missions.f.checkpointTtl', editing.checkpoint_ttl_hours, v => patch({ checkpoint_ttl_hours: v }), 1)}
              {check('missions.f.weatherEffects', editing.weather_effects, v => patch({ weather_effects: v }))}
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0 }}>{t('missions.f.weatherEffects.hint')}</p>
            </div>
          </div>

          <div className="adminCard">
            <div className="adminCardHeader">{t('missions.card.rewards')}</div>
            <div style={card}>
              {check('missions.f.drops', editing.drops_enabled, v => patch({ drops_enabled: v }))}
              {check('missions.f.xp', editing.xp_enabled, v => patch({ xp_enabled: v }))}
              {check('missions.f.keepRewards', editing.keep_rewards_on_loss, v => patch({ keep_rewards_on_loss: v }))}
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0 }}>{t('missions.f.keepRewards.hint')}</p>
              <label>
                <span className="formLabel">{t('missions.f.completionItem')}</span>
                {itemSelect(editing.completion_item_id, v => patch({ completion_item_id: v }))}
              </label>
            </div>
          </div>
        </div>

        <div className="adminCard">
          <div className="adminCardHeader">{t('missions.card.levels')}</div>
          <div style={{ padding: '12px 18px', overflowX: 'auto' }}>
            <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 0 }}>{t('missions.lvl.hint')}</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-dim)' }}>
                  <th>#</th>
                  <th>{t('missions.lvl.map')}</th>
                  <th>{t('missions.lvl.monsterLevel')}</th>
                  <th>{t('missions.lvl.count')}</th>
                  <th>{t('missions.lvl.weather')}</th>
                  <th>{t('missions.lvl.reward')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {editing.levels.map((lvl, i) => (
                  <tr key={i}>
                    <td style={{ padding: '6px 6px 6px 0', fontWeight: 700 }}>
                      {i + 1}
                      {editing.checkpoint_every > 0 && (i + 1) % editing.checkpoint_every === 0 && ' 🚩'}
                    </td>
                    <td>
                      <select className="formInput" value={lvl.map_id ?? ''} onChange={e => patchLevel(i, { map_id: e.target.value || null })}>
                        <option value="">{t('missions.random')}</option>
                        {maps.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="number" min={1} className="formInput" style={{ width: 80 }} value={lvl.monster_level}
                             onChange={e => patchLevel(i, { monster_level: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                    </td>
                    <td>
                      <input type="number" min={1} max={6} className="formInput" style={{ width: 80 }} value={lvl.monster_count ?? ''}
                             onChange={e => patchLevel(i, { monster_count: e.target.value === '' ? null : Math.min(6, Math.max(1, parseInt(e.target.value, 10) || 1)) })} />
                    </td>
                    <td>
                      <select className="formInput" value={lvl.weather_type} onChange={e => patchLevel(i, { weather_type: e.target.value })}>
                        {WEATHERS.map(w => <option key={w} value={w}>{t(`weatherType.${w}` as TranslationKey)}</option>)}
                      </select>
                    </td>
                    <td>{itemSelect(lvl.reward_item_id, v => patchLevel(i, { reward_item_id: v }))}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn" disabled={i === 0} onClick={() => moveLevel(i, -1)}>↑</button>
                      <button className="btn" disabled={i === editing.levels.length - 1} onClick={() => moveLevel(i, 1)}>↓</button>
                      <button className="btn" disabled={editing.levels.length === 1}
                              onClick={() => patch({ levels: editing.levels.filter((_, j) => j !== i) })}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn" style={{ marginTop: 12 }}
                    onClick={() => patch({ levels: [...editing.levels, newLevel((editing.levels.at(-1)?.monster_level ?? 0) + 1)] })}>
              {t('missions.lvl.add')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
